import { FormControl, FormLabel, Heading, Stack, Switch, useToast } from '@chakra-ui/react';
import React, { useCallback, useEffect, useReducer } from 'react';
import { serverUrl } from '../config';
import { Fetching } from './Fetching';
import { useDebounce } from './hooks/useDebounce';
import { useAuthenticateApiCall } from './hooks/useAuthenticateApiCall';

interface NotificationPreferences {
  'weekly-unread-notification': boolean;
}

const route = `${serverUrl}/api/preferences/`;

const fetchNotificationPreferences = (token: string) => () =>
  fetch(route, {
    method: 'get',
    headers: {
      'Content-Type': 'application/json',
      authorization: `Token ${token}`,
    },
  }).then((res) => res.json());

const syncNotificationPreferences =
  (token: string) => (notificationPreferences: NotificationPreferences) =>
    fetch(route, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Token ${token}`,
      },
      body: JSON.stringify(notificationPreferences),
    }).then((res) => res.json());

const useToastOnSuccess = () => {
  const toast = useToast();

  return useCallback(() => {
    toast({
      title: 'Success',
      description: 'Your notification preferences have been updated',
      status: 'success',
      position: 'top',
      duration: 1200,
    });
  }, [toast]);
};

type NotificationSettingsFormProps = {
  formState: NotificationPreferences;
  onChange: (newState: NotificationPreferences) => void;
};

const NotificationSettingsForm = ({ formState, onChange }: NotificationSettingsFormProps) => (
  <div style={{ width: '100%' }}>
    <Heading size="md" mb="4">
      Notification Settings
    </Heading>
    <Stack spacing={4}>
      <FormControl display="flex" justifyContent="space-between" alignItems="center">
        <FormLabel htmlFor="weekly-reminder-emails" mb="0">
          Receive weekly unread reminder
        </FormLabel>
        <Switch
          isChecked={formState['weekly-unread-notification']}
          onChange={(_) => {
            onChange({
              'weekly-unread-notification': !formState['weekly-unread-notification'],
            });
          }}
          id="weekly-reminder-emails"
          colorScheme="purple"
        />
      </FormControl>
    </Stack>
  </div>
);

type ReducerState = {
  status: 'uninitialised' | 'initialised' | 'out-of-sync' | 'synced';
  preferences: NotificationPreferences | undefined;
};

type NotificationsPreferencesAction =
  | { type: 'preferencesLoaded'; preferences: NotificationPreferences }
  | { type: 'userChangedSetting'; newPreferences: NotificationPreferences }
  | { type: 'preferencesPersisted' };

const reducer = (state: ReducerState, action: NotificationsPreferencesAction): ReducerState => {
  switch (action.type) {
    case 'preferencesLoaded':
      return { preferences: action.preferences, status: 'initialised' };
    case 'userChangedSetting':
      return { preferences: action.newPreferences, status: 'out-of-sync' };
    case 'preferencesPersisted':
      return { ...state, status: 'synced' };
  }
};

type Dispatch = (action: NotificationsPreferencesAction) => void;

const useSyncPreferences = (state: ReducerState, dispatch: Dispatch) => {
  const onSuccess = useToastOnSuccess();
  const { status, preferences } = useDebounce(state, 200);
  const authedSyncNotificationPreferences = useAuthenticateApiCall(syncNotificationPreferences);

  useEffect(() => {
    if (status === 'out-of-sync' && preferences) {
      authedSyncNotificationPreferences(preferences).then(() => {
        dispatch({ type: 'preferencesPersisted' });
        onSuccess();
      });
    }
  }, [status, preferences, dispatch, onSuccess, authedSyncNotificationPreferences]);
};

const useInitialFetch = (state: ReducerState, dispatch: Dispatch) => {
  const authedFetchNotificationPreferences = useAuthenticateApiCall(fetchNotificationPreferences);
  useEffect(() => {
    if (state.status === 'uninitialised') {
      authedFetchNotificationPreferences().then((p) =>
        dispatch({ type: 'preferencesLoaded', preferences: p })
      );
    }
  }, [state, authedFetchNotificationPreferences, dispatch]);
};

export const NotificationSettings = () => {
  const [state, dispatch] = useReducer(reducer, {
    status: 'uninitialised',
    preferences: undefined,
  });

  useInitialFetch(state, dispatch);
  useSyncPreferences(state, dispatch);

  return (
    <Fetching isFetching={state.status === 'uninitialised'}>
      {state.preferences && (
        <NotificationSettingsForm
          formState={state.preferences}
          onChange={(p) => dispatch({ type: 'userChangedSetting', newPreferences: p })}
        />
      )}
    </Fetching>
  );
};
